import { renderToString } from 'react-dom/server';
import React, { ReactElement } from 'react';
import { Cell, CellValueType } from '../../../Interface/Hook/Interest/InterestTableDTO';
import { PropertyAttributes } from '../../../Interface/Response/Interest/PropertyAttributes';
import { CellDecorator, RowDecorator } from '../../../Enum/TableDecorator';
import { PropertyAttribute } from '../../../Component/Interest/Table/Decorator/ProductProperties/PropertyAttribute';
import { formatPercent } from '../../../Helpers/Percent';
import { Interest } from '../../../Interface/Response/Interest/Interest';
import { NewsType } from '../../../Enum/Interest/NewsType';
import { Cell as AcceptationGuideCell } from '../../../Interface/Response/Interest/AcceptationGuideData';

export const isPropertyAttributes = (value: CellValueType): value is PropertyAttributes =>
    (value?.hasOwnProperty('type') && value?.hasOwnProperty('value')) || false;

export const isInterest = (value: CellValueType): value is Interest =>
    value?.hasOwnProperty('percentage') || false;

export const isAcceptationGuideData = (value: CellValueType): value is AcceptationGuideCell =>
    (value?.hasOwnProperty('label') && value?.hasOwnProperty('acceptationGuideData')) || false;

export const getCellValue = (cell: Cell): string => {
    if (typeof cell.value === 'string') {
        return cell.value;
    }
    if (typeof cell.value === 'number') {
        return cell.value.toString();
    }
    if (isPropertyAttributes(cell.value)) {
        return cell.value.value;
    }

    return renderToString(cell.value as ReactElement);
};

const decorateProductProperty = (value: PropertyAttributes, decorator?: CellDecorator): string => {
    let content: string;

    if (decorator === CellDecorator.ProductProperty) {
        content = getCellValue({ value: <PropertyAttribute type={value.type} value={value.value} /> });

        if (value.remark !== undefined) {
            content += '\n\n   ' + value.remark;
        }

        return content;
    }

    return value.value;
};

export const decorateCellContent = (
    value: CellValueType,
    cellDecorator?: CellDecorator,
    rowDecorator?: RowDecorator,
): string => {
    if (isPropertyAttributes(value)) {
        if (value.infoExpanded) {
            return value.info || '';
        }
        return decorateProductProperty(value, cellDecorator);
    }

    if (isInterest(value)) {
        let cellContent = formatPercent(value.percentage) || '';
        if (value.upcomingChange?.type === NewsType.LOWERED || value.upcomingChange?.type === NewsType.UPPED) {
            cellContent = `[${value.upcomingChange?.type}]${formatPercent(value.percentage)}`;
        }

        return value.onlyForExistingCustomers ? `[${CellDecorator.Strikethrough}]${cellContent}` : cellContent;
    }

    if (typeof value === 'string') {
        if (rowDecorator === RowDecorator.ScopeRow) {
            return value.toUpperCase();
        }
        if (cellDecorator === CellDecorator.CompanyIcon) {
            return `[${cellDecorator}]${value || ''}`;
        }

        return value;
    }

    if (isAcceptationGuideData(value)) {
        return value.label;
    }

    if (value === null) {
        return '';
    }

    throw new Error('Type is not implemented yet.');
};
